import type { FeatureFlagsDevToolsModel } from '.';
import { launchDarklyFlagUrl } from './launchDarklyFlagUrl';
import { signal } from '@preact/signals-react';
import type { ConfigService } from '@thinkalpha/ui-ioc/core/types/ConfigService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';

const STORAGE_KEY = 'featureFlagsDevTools.isOpen';

function loadOpenState(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

function saveOpenState(isOpen: boolean): void {
    try {
        localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
        // Ignore storage errors
    }
}

@injectable()
export class FeatureFlagsDevToolsModelImpl implements FeatureFlagsDevToolsModel {
    readonly #logger: Logger;
    readonly #isOpen;
    readonly #configService: ConfigService;

    constructor(@inject('Logger') logger: Logger, @inject('ConfigService') configService: ConfigService) {
        this.#logger = logger.getSubLogger({ name: 'FeatureFlagsDevToolsModel' });
        this.#configService = configService;
        const initialOpenState = loadOpenState();
        if (TRACE) {
            this.#logger.trace('Loaded initial open state', { isOpen: initialOpenState });
        }
        this.#isOpen = signal(initialOpenState);
    }

    get isOpen() {
        return this.#isOpen;
    }

    toggleOpen(): void {
        this.#isOpen.value = !this.#isOpen.value;
        if (TRACE) {
            this.#logger.trace('Toggled open state', { isOpen: this.#isOpen.value });
        }
        saveOpenState(this.#isOpen.value);
    }

    close(): void {
        this.#isOpen.value = false;
        if (TRACE) {
            this.#logger.trace('Closed devtools', { isOpen: false });
        }
        saveOpenState(false);
    }

    launchDarklyFlagUrl(flagKey: string): string {
        return launchDarklyFlagUrl(flagKey, this.#configService.launchDarklyConfig.environmentKey);
    }
}
